import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Trash2, Plus } from 'lucide-react';
import useTrainingPlanStore from '../../stores/trainingPlanStore';
import { useTheme } from '../../context/ThemeContext';

export default function WorkoutCard({ workoutId, weekId, slotIndex }) {
 const { themeClasses } = useTheme();
 const workout = useTrainingPlanStore(state => state.workouts[workoutId]);
 const exercises = useTrainingPlanStore(state => state.exercises);
 const openWorkoutDrawer = useTrainingPlanStore(state => state.openWorkoutDrawer);
 const removeWorkout = useTrainingPlanStore(state => state.removeWorkout);
 const language = localStorage.getItem('language') || 'english';

 // We sort by an ID combining week, slot, and workout so we can move it
 const draggableId = `${weekId}_${slotIndex}_${workoutId}`;
 
 const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
 id: draggableId,
 data: { type: 'workout_card', workoutId, weekId, slotIndex }
 });

 const style = {
 transform: CSS.Transform.toString(transform),
 transition,
 zIndex: isDragging ? 20 : 1,
 opacity: isDragging ? 0.8 : 1,
 };

 if (!workout) return null;

 const topExercises = workout.exerciseIds.slice(0, 3).map(id => exercises[id]);
 const hasMore = workout.exerciseIds.length > 3;

 const handleAddExercise = (e) => {
 e.stopPropagation();
 openWorkoutDrawer(workoutId, { search: true });
 };

 const handleDelete = (e) => {
 e.stopPropagation();
 removeWorkout(workoutId);
 };

 return (
 <div 
 ref={setNodeRef}
 style={style}
 className={`h-full flex flex-col rounded-md border border-line dark:border-line-dark bg-surface dark:bg-surface-dark hover: transition-all cursor-pointer overflow-hidden group ${isDragging ? 'ring-2 ring-forest scale-105' : ''}`}
 onClick={() => openWorkoutDrawer(workoutId)}
 >
 <div className="flex items-start p-2.5 sm:p-3 border-b border-line dark:border-line-dark bg-surface dark:bg-surface-dark">
 <div 
 {...attributes} 
 {...listeners} 
 className="p-1 -ms-1 me-1 text-ink-muted hover:text-ink-muted dark:hover:text-ink-muted-dark cursor-grab active:cursor-grabbing rounded touch-none"
 onClick={(e) => e.stopPropagation()}
 >
 <GripVertical size={16} />
 </div>
 <h4 className={`font-bold text-sm sm:text-base leading-tight truncate flex-1 ${themeClasses.textPrimary}`}>
 {workout.day_name || (language === 'hebrew' ? 'אימון חדש' : 'New Workout')}
 </h4>
 <button
 onClick={handleDelete}
 aria-label={language === 'hebrew' ? 'הסר אימון' : 'Remove Workout'}
 className="p-2 -m-1 ms-1 text-ink-muted hover:text-red-500 dark:hover:text-red-400 lg:opacity-0 lg:group-hover:opacity-100 focus-visible:opacity-100 transition-all rounded hover:bg-red-50 dark:hover:bg-red-900/30"
 title={language === 'hebrew' ? 'הסר אימון' : 'Remove Workout'}
 >
 <Trash2 size={16} />
 </button>
 </div>
 
 <div className="p-2 sm:p-3 flex-1 flex flex-col gap-1.5 overflow-hidden">
 {workout.exerciseIds.length === 0 ? (
 <div className="text-xs text-ink-muted dark:text-ink-muted italic mt-1">
 {language === 'hebrew' ? 'אין תרגילים' : 'No exercises'}
 </div>
 ) : (
 <>
 {topExercises.map((ex, idx) => (
 <div key={ex?.exercise_instance_id || idx} className="text-xs sm:text-sm text-ink dark:text-ink-dark truncate">
 <span className="font-semibold opacity-60 me-1">{String.fromCharCode(65 + idx)})</span>
 {ex?.exercise_name || '—'}
 </div>
 ))}
 {hasMore && (
 <div className="text-xs text-forest dark:text-forest-ondark font-medium mt-0.5">
 + {workout.exerciseIds.length - 3} {language === 'hebrew' ? 'עוד' : 'more'}
 </div>
 )}
 </>
 )}
 <button
 type="button"
 onClick={handleAddExercise}
 className="mt-auto min-h-11 w-full inline-flex items-center justify-center gap-1.5 rounded-lg border border-line dark:border-line bg-surface dark:bg-surface/[0.04] text-[11px] font-bold uppercase tracking-wide text-ink-muted dark:text-ink-dark hover:bg-forest/15 hover:text-forest dark:hover:text-forest-ondark transition-[transform,opacity] duration-200"
 >
 <Plus size={14} strokeWidth={2.5} />
 {language === 'hebrew' ? 'הוסף תרגיל' : 'Add Exercise'}
 </button>
 </div>
 </div>
 );
}
